import React, { useEffect, useState, useCallback } from 'react';
import ChartTooltip from '../components/ChartTooltip';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { getDevices, getTech, parseReport } from '../api';
import ChartCard from '../components/ChartCard';

const COLORS = ['#38bdf8', '#818cf8', '#34d399', '#f472b6', '#fb923c', '#fbbf24'];

const RPLABEL = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
  const R = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * R);
  const y = cy + r * Math.sin(-midAngle * R);
  if (percent < 0.05) return null;
  return <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>{(percent * 100).toFixed(0)}%</text>;
};

export default function Technology({ propertyId, startDate, endDate }) {
  const [devices, setDevices] = useState([]);
  const [os, setOs] = useState([]);
  const [browser, setBrowser] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const [d, t] = await Promise.all([
        getDevices(propertyId, startDate, endDate),
        getTech(propertyId, startDate, endDate),
      ]);
      setDevices(parseReport(d.data).sort((a, b) => b.sessions - a.sessions));
      setOs(parseReport(t.data.os).sort((a, b) => b.sessions - a.sessions).slice(0, 10));
      setBrowser(parseReport(t.data.browser).sort((a, b) => b.sessions - a.sessions).slice(0, 10));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [propertyId, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 40, color: 'var(--text3)', textAlign: 'center' }}>Loading technology data...</div>;

  return (
    <div style={styles.grid}>
      <ChartCard title="Device Category" subtitle="Mobile vs Desktop vs Tablet">
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie data={devices} cx="50%" cy="50%" innerRadius={60} outerRadius={110} dataKey="sessions" nameKey="deviceCategory" labelLine={false} label={RPLABEL}>
              {devices.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
            <Legend formatter={(v, e) => e?.payload?.deviceCategory || v} wrapperStyle={{ fontSize: 12, color: 'var(--text2)' }} />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Device Stats" subtitle="Sessions per device">
        <div style={styles.devCards}>
          {devices.map((d, i) => {
            const icon = d.deviceCategory === 'mobile' ? '📱' : d.deviceCategory === 'desktop' ? '🖥' : '📟';
            return (
              <div key={i} style={{ ...styles.devCard, borderColor: COLORS[i] + '44' }}>
                <div style={{ fontSize: 32 }}>{icon}</div>
                <div style={{ fontSize: 13, color: COLORS[i], fontWeight: 700, textTransform: 'capitalize' }}>{d.deviceCategory}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>{d.sessions?.toLocaleString()}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>sessions</div>
              </div>
            );
          })}
        </div>
      </ChartCard>

      <ChartCard title="Top Browsers" subtitle="Browser sessions" span={2}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={browser} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="browser" tick={{ fill: '#475569', fontSize: 10, angle: -20, textAnchor: 'end' }} />
            <YAxis tick={{ fill: 'var(--text3)', fontSize: 10 }} />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey="sessions" name="Sessions" radius={[4, 4, 0, 0]}>
              {browser.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Operating Systems" subtitle="OS sessions" span={2}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={os} layout="vertical" margin={{ top: 0, right: 20, left: 60, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
            <XAxis type="number" tick={{ fill: 'var(--text3)', fontSize: 10 }} />
            <YAxis type="category" dataKey="operatingSystem" tick={{ fill: 'var(--text2)', fontSize: 11 }} width={55} />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey="sessions" name="Sessions" radius={[0, 4, 4, 0]}>
              {os.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

const styles = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 },
  devCards: { display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 },
  devCard: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '16px', border: '1px solid', borderRadius: 12, background: 'var(--surface2)' },
};
